<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import { vMaska } from "maska/vue";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VInput from "@magnit/core/src/components/VInput/VInput.vue";
import VButton from "@magnit/core/src/components/VButton/VButton.vue";
import VYaCaptcha from "@magnit/core/src/containers/VYaCaptcha/VYaCaptcha.vue";
import type { IModalAuthPhoneFormProps } from "../VModalAuth.types";
import {
  EVENT_CAPCTHA_CLICK,
  EVENT_PHONE_CLICK,
  EVENT_PHONE_SUBMIT,
  EVENT_PHONE_VIEW,
  LABEL_BUTTON_NEXT,
  LABEL_INCORRECT_PHONE,
} from "../constants";
import type { IYaCaptchaExpose } from "@magnit/core/src/containers/VYaCaptcha/VYaCaptcha.types";
import { waitForValue } from "@magnit/core/src/utilities/async";

const props = defineProps<IModalAuthPhoneFormProps>();
const emit = defineEmits<{
  submit: [string];
  analytics: [{ eventName: string; params?: Record<string, unknown> }];
}>();

const captchaLoading = ref(false);
const captchaToken = defineModel<string>("captchaToken", { required: true });
const captchaRef = ref<IYaCaptchaExpose | null>(null);
const captchaLoadError = ref("");
const phoneNumber = ref<string>(props.phone || "");
const inputPhoneRef = ref<null | InstanceType<typeof VInput>>(null);

const phoneValue = computed(() => phoneNumber.value.replaceAll(/[\D]/gi, ""));
const validPhone = computed(() => phoneValue.value.length === 10);
const submitDisabled = computed(() => {
  const isDisabled = props.pending || !validPhone.value;
  if (props.invisibleCaptcha) return isDisabled || captchaLoading.value;
  return isDisabled || !captchaToken.value;
});
const errorLabel = computed(() => props.errorText || LABEL_INCORRECT_PHONE);

const onYaCaptchaTokenChange = (token?: string) => {
  emit("analytics", { eventName: EVENT_CAPCTHA_CLICK });
  if (!token && captchaToken.value === token) {
    return;
  }
  captchaToken.value = token ?? "";
};
const onClickInput = () => {
  emit("analytics", { eventName: EVENT_PHONE_CLICK });
};
const onSubmit = async () => {
  emit("analytics", { eventName: EVENT_PHONE_SUBMIT });
  captchaLoading.value = true;
  if (!props.disableCaptcha) {
    try {
      captchaLoadError.value = "";
      await captchaRef.value?.load();
      if (props.invisibleCaptcha) {
        captchaRef.value?.execute();
        await waitForValue(captchaToken);
      }
    } catch {
      captchaLoadError.value =
        "Ошибка загрузки капчи. Попробуйте снова или перезагрузите страницу.";
    }
  }
  if (captchaToken.value || props.disableCaptcha) {
    emit("submit", phoneValue.value);
  }
  captchaLoading.value = false;
};
onMounted(() => {
  emit("analytics", { eventName: EVENT_PHONE_VIEW });
  inputPhoneRef.value?.focus();
});
</script>

<template>
  <section class="auth-phone-form" data-test-id="auth-modal">
    <div class="auth-phone-form__field">
      <VInput
        ref="inputPhoneRef"
        v-model="phoneNumber"
        v-maska
        :has-error="error"
        :disabled="pending"
        data-maska="### ###-##-##"
        data-test-id="phone-row"
        inputmode="tel"
        @click="onClickInput"
        @keyup.enter="onSubmit"
      />
      <div v-if="error" class="auth-phone-form__field--error">
        <VText font="caption-large-regular" color="system-error">
          {{ captchaLoadError || errorLabel }}
        </VText>
      </div>
    </div>
    <VYaCaptcha
      v-if="!props.disableCaptcha"
      ref="captchaRef"
      class="auth-phone-form__captcha"
      :invisible="invisibleCaptcha"
      :hide-shield="invisibleCaptcha"
      :no-auto-execute="invisibleCaptcha"
      :captcha-key="captchaKey"
      @token-changed="onYaCaptchaTokenChange"
    />
    <VButton
      full-width
      :loading="pending || captchaLoading"
      :disabled="submitDisabled"
      @click="onSubmit"
    >
      {{ LABEL_BUTTON_NEXT }}
    </VButton>
  </section>
</template>

<style lang="postcss" scoped>
.auth-phone-form {
  &__captcha {
    margin-bottom: var(--pl-unit-x5);
  }

  &__field {
    margin-bottom: var(--pl-unit-x4);

    :deep(.pl-input) {
      &::before {
        content: "+7 ";
      }
    }

    &--error {
      display: flex;
      padding: 0 var(--pl-unit-x4);
    }
  }
}
</style>
